import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { WhatsappLogo, ArrowClockwise, FunnelSimple } from "@phosphor-icons/react";

const PURPOSE_LABEL = {
  payment_link: "Link Pembayaran",
  payment_success: "Struk Sukses",
  reservation_confirmation: "Konfirmasi Reservasi",
  manual_payment_received: "Pembayaran Diterima (Manual)",
  staff_assignment: "Penugasan Petugas",
  auto_cancel: "Pembatalan Otomatis",
  clinara_autoreply: "Balasan CLINARA",
  test: "Tes Koneksi",
  manual: "Manual",
};

const SOURCE_LABEL = {
  single: "Tunggal",
  multi_child: "Multi — Anak Layanan",
  system: "Sistem/Non-reservasi",
};

// Presentation-only mapping (DB value is NEVER changed). Unknown codes stay generic
// so no sensitive technical detail leaks to the panel.
const FAIL_REASON = {
  staff_not_found: { label: "Data petugas tidak ditemukan", hint: "Periksa nama petugas di Master Petugas" },
  phone_missing: { label: "Nomor WhatsApp belum diisi", hint: "Perbarui nomor di Master Petugas" },
  phone_invalid: { label: "Format nomor WhatsApp tidak valid", hint: "Perbarui nomor di Master Petugas" },
  gateway_error: { label: "WACloud gagal menerima pesan", hint: "Periksa koneksi WACloud atau hubungi admin teknis" },
  device_not_connected: { label: "Perangkat WhatsApp tidak terhubung", hint: "Periksa koneksi WACloud atau hubungi admin teknis" },
  device_unavailable: { label: "Perangkat WhatsApp tidak terhubung", hint: "Periksa koneksi WACloud atau hubungi admin teknis" },
  timeout: { label: "Pengiriman melewati batas waktu", hint: "Periksa koneksi WACloud atau hubungi admin teknis" },
};

const failInfo = (reason) => {
  if (!reason) return null;
  return FAIL_REASON[reason] || { label: "Gangguan pengiriman", hint: null };
};

const statusChip = (s) => {
  const map = {
    accepted_by_gateway: "bg-sky-100 text-sky-700",
    delivered: "bg-emerald-100 text-emerald-700",
    sent: "bg-emerald-100 text-emerald-700",
    queued: "bg-slate-100 text-slate-600",
    stubbed: "bg-amber-100 text-amber-700",
    failed: "bg-rose-100 text-rose-700",
    invalid_phone: "bg-rose-100 text-rose-700",
  };
  return map[s] || "bg-slate-100 text-slate-600";
};

const EMPTY = { start: "", end: "", purpose: "", status: "", source: "" };

export default function WaLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ limit: "200" });
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    api.get(`/admin/wa-messages?${p.toString()}`)
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="mx-auto max-w-5xl px-4 py-6" data-testid="wa-log-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-800">
            <WhatsappLogo size={24} weight="fill" className="text-[#25D366]" /> Log WhatsApp
          </h1>
          <p className="text-sm text-slate-500">Riwayat pengiriman notifikasi WhatsApp (nomor disamarkan).</p>
        </div>
        <button data-testid="wa-log-refresh" onClick={load} className="inline-flex items-center gap-1.5 rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200">
          <ArrowClockwise size={14} weight="bold" /> Muat Ulang
        </button>
      </div>

      {/* Filters */}
      <div data-testid="wa-log-filters" className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border border-rose-100/70 bg-white p-4">
        <div className="flex items-center gap-1.5 text-xs font-bold text-slate-500">
          <FunnelSimple size={16} weight="bold" /> Filter
        </div>
        <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-500">
          Dari Tanggal
          <input data-testid="wa-filter-start" type="date" value={filters.start} onChange={setF("start")}
            className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-700" />
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-500">
          Sampai Tanggal
          <input data-testid="wa-filter-end" type="date" value={filters.end} onChange={setF("end")}
            className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-700" />
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-500">
          Jenis
          <select data-testid="wa-filter-purpose" value={filters.purpose} onChange={setF("purpose")}
            className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-700">
            <option value="">Semua Jenis</option>
            {Object.entries(PURPOSE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-500">
          Status
          <select data-testid="wa-filter-status" value={filters.status} onChange={setF("status")}
            className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-700">
            <option value="">Semua Status</option>
            <option value="accepted_by_gateway">accepted_by_gateway</option>
            <option value="delivered">delivered</option>
            <option value="stubbed">stubbed</option>
            <option value="failed">failed</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-500">
          Sumber
          <select data-testid="wa-filter-source" value={filters.source} onChange={setF("source")}
            className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-700">
            <option value="">Semua Sumber</option>
            <option value="single">Tunggal</option>
            <option value="multi_child">Multi — Anak Layanan</option>
            <option value="system">Sistem/Non-reservasi</option>
          </select>
        </label>
        <button data-testid="wa-filter-reset" onClick={() => setFilters(EMPTY)}
          className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200">
          Reset
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-rose-100/70 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-xs text-slate-500">
            <tr>
              <th className="px-4 py-2.5 font-semibold">Waktu</th>
              <th className="px-4 py-2.5 font-semibold">Tujuan</th>
              <th className="px-4 py-2.5 font-semibold">Jenis</th>
              <th className="px-4 py-2.5 font-semibold">Sumber</th>
              <th className="px-4 py-2.5 font-semibold">Status</th>
              <th className="px-4 py-2.5 font-semibold">Alasan Gagal</th>
              <th className="px-4 py-2.5 font-semibold">Dikirim Oleh</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Memuat…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Belum ada log.</td></tr>}
            {rows.map((m) => (
              <tr key={m.id} data-testid={`wa-log-row-${m.id}`} className="border-b border-slate-50 last:border-0">
                <td className="px-4 py-2.5 text-slate-600">{String(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-2.5 font-mono text-slate-700">{m.target_masked}</td>
                <td className="px-4 py-2.5 text-slate-600">{PURPOSE_LABEL[m.purpose] || m.purpose}</td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{SOURCE_LABEL[m.source] || SOURCE_LABEL.system}</td>
                <td className="px-4 py-2.5"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${statusChip(m.status)}`}>{m.status}</span></td>
                <td className="px-4 py-2.5" data-testid={`wa-log-failreason-${m.id}`}>
                  {(() => {
                    const f = failInfo(m.fail_reason);
                    if (!f) return <span className="text-xs text-slate-400">-</span>;
                    return (
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold text-rose-600">{f.label}</span>
                        {f.hint && <span className="text-[11px] text-slate-400">{f.hint}</span>}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{m.sent_by || (m.mode === "mock" ? "sistem (uji)" : "sistem")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
